import { ref } from 'vue'
import { defineNuxtRouteMiddleware } from '#app/composables/router'
import { useTrackingEvents } from '~/composables'
import { useShopGenderCookie } from '~/composables/useShopGenderCookie'
import { useBasket, useCurrentShop } from '#storefront/composables'
import { useGenderMenuItems } from '~/composables/useGenderMenuItems'

const TIMEOUT_DELAY = 500
const firstLoad = ref(true)
const oldPageTitle = ref('')

export default defineNuxtRouteMiddleware((to, from) => {
  if (import.meta.server) {
    return
  }

  const oldSlugs = from?.params?.slug
  const slugs = to?.params?.slug
  const isPDP = to?.meta?.key === 'PDP'
  const isSizeChange = oldSlugs && slugs && slugs.length === 2 && isPDP

  if (isSizeChange) {
    // if is the same product slug, we don't need to track the page view
    if (oldSlugs[0] === slugs[0]) {
      return
    }
  }

  const fromScope = from?.params?.scope as string
  const toScope = to?.params?.scope as string

  const generateMeta = (route: any, scope: any) => {
    const { pageType, contentType, ...otherMeta } = route?.meta || {}

    // Only apply homepage-specific logic if this is actually a homepage route
    const isHomepage =
      pageType === 'homepage' || pageType?.startsWith('homepage-')

    if (isHomepage) {
      return {
        ...otherMeta,
        pageType: scope ? `homepage-${scope}` : 'homepage',
        contentType: 'Content Page',
      }
    }

    // For non-homepage routes, preserve the original pageType and contentType
    return {
      ...otherMeta,
      pageType,
      contentType,
    }
  }

  const { trackPageView, trackBasketItems } = useTrackingEvents()
  const { items: basketItems } = useBasket()

  // Check if the current route will be redirected by scope middleware
  // If so, skip tracking this route to avoid double tracking
  const willRedirect = (() => {
    if (import.meta.server) {
      return false
    }

    const { cookie: currentMenu } = useShopGenderCookie()
    const { isValidGender } = useGenderMenuItems()
    const shopLocale = useCurrentShop()

    const validScopes = isValidGender(currentMenu.value.toLowerCase())

    const validCurrentMenu = currentMenu.value && validScopes

    // Same logic as scope.global.ts middleware
    return (
      validCurrentMenu &&
      !to.params.scope &&
      (to.path === `/${shopLocale.value.path}` || to.path === '/')
    )
  })()

  // Skip tracking if we know this route will be redirected
  if (willRedirect) {
    return
  }

  // We need to add delay because title is not properly retrieved from "document".
  setTimeout(() => {
    const currentPageTitle = window.document.title

    trackPageView({
      pageMeta: currentPageTitle,
      from: firstLoad.value
        ? ({} as any)
        : ({
            ...from,
            meta: {
              ...generateMeta(from, fromScope),
              pageMeta: oldPageTitle.value,
            },
          } as any),
      to: {
        ...to,
        meta: {
          ...generateMeta(to, toScope),
          pageMeta: currentPageTitle,
        },
      } as any,
    })
    trackBasketItems(basketItems.value || [], 'page load')

    firstLoad.value = false
    oldPageTitle.value = currentPageTitle
  }, TIMEOUT_DELAY)
})
